import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronRight,
  Home,
  Search,
  X,
  Filter,
  ChevronDown,
  Check,
} from 'lucide-react';
import PrintingFontStyles from './PrintingFontStyles';
import ProductDetailModal from './ProductDetailModal';
import { printingColors } from '../../lib/printing-theme';
import {
  allProducts,
  PrintingProduct,
} from '../../lib/printing-products';
import {
  PRINT_CATEGORIES,
  getPrintCategories,
  sortByPopularity,
} from '../../data/printing/catalogue';

interface PrintingBrowserProps {
  initialProductSlug?: string;
}

const PrintingBrowser: React.FC<PrintingBrowserProps> = ({ initialProductSlug }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // `?q=` lets the header mega menu open the browser pre-filtered by name.
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  
  const categoryParam = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(
    categoryParam && PRINT_CATEGORIES.some(c => c.id === categoryParam) ? categoryParam : 'all'
  );
  
  const [modalProduct, setModalProduct] = useState<PrintingProduct | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Collapsible sections state
  const [sectionsOpen, setSectionsOpen] = useState({
    category: true,
  });

  // Open modal for initial product slug if provided
  useEffect(() => {
    if (initialProductSlug) {
      const product = allProducts.find(p => p.slug === initialProductSlug);
      if (product) {
        setModalProduct(product);
      }
    }
  }, [initialProductSlug]);

  // Update URL when category changes
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    let products = [...allProducts];

    // Filter by category
    if (selectedCategory !== 'all') {
      products = products.filter(product => (getPrintCategories(product) as string[]).includes(selectedCategory));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      products = products.filter(product =>
        product.title.toLowerCase().includes(query) ||
        product.short_description.toLowerCase().includes(query)
      );
    }

    // Sort by popularity
    return sortByPopularity(products);
  }, [selectedCategory, searchQuery]);

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSearchQuery('');
    searchParams.delete('category');
    setSearchParams(searchParams, { replace: true });
  };

  const hasActiveFilters = selectedCategory !== 'all' || searchQuery.trim() !== '';

  return (
    <>
      <PrintingFontStyles />

      <div className="min-h-screen relative isolate" style={{ backgroundColor: '#0b0b0b' }}>
        {/* Texture pinned to the viewport: stretched over the whole page
            height, the 2048px image scaled up ~3x and looked pixelated. */}
        <div
          aria-hidden="true"
          className="fixed inset-0 -z-10 pointer-events-none"
          style={{
            backgroundImage: 'url(/printing/matt-black.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Header */}
        <header
          className="relative z-10 py-6 md:py-8 px-6 md:px-8 lg:px-12 border-b border-white/10"
        >
          <div className="max-w-[1600px] mx-auto relative z-10">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-white transition-colors">
                <Home className="w-4 h-4" />
              </Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/printing" className="hover:text-white transition-colors">
                Printing
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white">All Products</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="hearns-font text-3xl md:text-4xl text-white mb-1">
                  PRINT PRODUCTS
                </h1>
                <p className="neuzeit-light-font text-white/70">
                  Browse our complete range
                </p>
              </div>

              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 rounded-[10px] border border-white/10 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-8 lg:px-12 py-8">
          <div className="flex gap-8">
            {/* Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div
                className="sticky top-8 rounded-[15px] overflow-hidden flex flex-col border border-white/10"
                style={{ backgroundColor: '#1c1c1c', maxHeight: 'calc(100vh - 120px)' }}
              >
                <div className="p-4 border-b border-white/10 flex-shrink-0">
                  <h2 className="font-semibold text-white neuzeit-font">Filters</h2>
                </div>

                {/* Active Filters Display */}
                {hasActiveFilters && (
                  <div 
                    className="px-4 py-3 border-b border-white/10 flex-shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-white/50 uppercase tracking-wide neuzeit-font">Active Filters</p>
                      <button
                        onClick={clearAllFilters}
                        className="text-xs font-medium px-2 py-1 rounded-md transition-all hover:opacity-80 border border-white/10 text-white"
                        style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedCategory !== 'all' && (
                        <button
                          onClick={() => handleCategoryChange('all')}
                          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80 neuzeit-font"
                          style={{ backgroundColor: printingColors.accent, color: 'white' }}
                        >
                          {PRINT_CATEGORIES.find(c => c.id === selectedCategory)?.label}
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Scrollable filter content */}
                <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30">

                {/* Category */}
                <div className="border-b border-white/10">
                  <button
                    onClick={() => toggleSection('category')}
                    className="w-full p-5 flex items-center justify-between text-white hover:bg-white/5 transition-colors"
                  >
                    <span className="text-sm uppercase tracking-wide embossing-font">Category</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${sectionsOpen.category ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {sectionsOpen.category && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 space-y-1">
                          <button
                            onClick={() => handleCategoryChange('all')}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg neuzeit-font text-sm transition-all ${
                              selectedCategory === 'all'
                                ? 'text-white'
                                : 'text-white/70 hover:text-white hover:bg-white/5'
                            }`}
                            style={selectedCategory === 'all' ? { backgroundColor: printingColors.accent } : {}}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                                  selectedCategory === 'all'
                                    ? 'border-transparent bg-white/20'
                                    : 'border-white/30'
                                }`}
                              >
                                {selectedCategory === 'all' && <Check className="w-3 h-3" />}
                              </div>
                              All products
                            </div>
                            <span className="text-xs text-white/50">{allProducts.length}</span>
                          </button>

                          {PRINT_CATEGORIES.map(category => {
                            const count = allProducts.filter(p => getPrintCategories(p).includes(category.id)).length;
                            return (
                              <button
                                key={category.id}
                                onClick={() => handleCategoryChange(category.id)}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg neuzeit-font text-sm transition-all ${
                                  selectedCategory === category.id
                                    ? 'text-white'
                                    : 'text-white/70 hover:text-white hover:bg-white/5'
                                }`}
                                style={selectedCategory === category.id ? { backgroundColor: printingColors.accent } : {}}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                                      selectedCategory === category.id
                                        ? 'border-transparent bg-white/20'
                                        : 'border-white/30'
                                    }`}
                                  >
                                    {selectedCategory === category.id && <Check className="w-3 h-3" />}
                                  </div>
                                  {category.label}
                                </div>
                                <span className="text-xs text-white/50">{count}</span>
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                </div>
              </div>
            </aside>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg neuzeit-font font-medium text-white"
              style={{ backgroundColor: printingColors.accent }}
            >
              <Filter className="w-5 h-5" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-white" />
              )}
            </button>

            {/* Mobile Filter Drawer */}
            <AnimatePresence>
              {sidebarOpen && (
                <>
                  {/* Backdrop */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSidebarOpen(false)}
                    className="lg:hidden fixed inset-0 bg-black/60 z-50"
                  />
                  {/* Drawer */}
                  <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="lg:hidden fixed left-0 top-0 bottom-0 w-80 max-w-[85vw] z-50 flex flex-col border-r border-white/10"
                    style={{ backgroundColor: '#1c1c1c' }}
                  >
                    <div className="p-5 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                      <h2 className="neuzeit-font font-semibold text-white text-lg">Filters</h2>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </div>

                    {/* Active Filters Display */}
                    {hasActiveFilters && (
                      <div 
                        className="px-4 py-3 border-b border-white/10 flex-shrink-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-white/50 uppercase tracking-wide neuzeit-font">Active Filters</p>
                          <button
                            onClick={() => {
                              clearAllFilters();
                              setSidebarOpen(false);
                            }}
                            className="text-xs font-medium px-2 py-1 rounded-md transition-all hover:opacity-80 border border-white/10 text-white"
                            style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedCategory !== 'all' && (
                            <button
                              onClick={() => handleCategoryChange('all')}
                              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80 neuzeit-font"
                              style={{ backgroundColor: printingColors.accent, color: 'white' }}
                            >
                              {PRINT_CATEGORIES.find(c => c.id === selectedCategory)?.label}
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Scrollable filter content */}
                    <div className="overflow-y-auto flex-1">
                      {/* Category */}
                      <div className="border-b border-white/10">
                        <button
                          onClick={() => toggleSection('category')}
                          className="w-full p-5 flex items-center justify-between text-white"
                        >
                          <span className="text-sm uppercase tracking-wide embossing-font">Category</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${sectionsOpen.category ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {sectionsOpen.category && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-5 pb-5 space-y-1">
                                <button
                                  onClick={() => handleCategoryChange('all')}
                                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg neuzeit-font text-sm transition-all ${
                                    selectedCategory === 'all'
                                      ? 'text-white'
                                      : 'text-white/70 hover:text-white hover:bg-white/5'
                                  }`}
                                  style={selectedCategory === 'all' ? { backgroundColor: printingColors.accent } : {}}
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                                        selectedCategory === 'all'
                                          ? 'border-transparent bg-white/20'
                                          : 'border-white/30'
                                      }`}
                                    >
                                      {selectedCategory === 'all' && <Check className="w-3 h-3" />}
                                    </div>
                                    All products
                                  </div>
                                  <span className="text-xs text-white/50">{allProducts.length}</span>
                                </button>

                                {PRINT_CATEGORIES.map(category => {
                                  const count = allProducts.filter(p => getPrintCategories(p).includes(category.id)).length;
                                  return (
                                    <button
                                      key={category.id}
                                      onClick={() => handleCategoryChange(category.id)}
                                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg neuzeit-font text-sm transition-all ${
                                        selectedCategory === category.id
                                          ? 'text-white'
                                          : 'text-white/70 hover:text-white hover:bg-white/5'
                                      }`}
                                      style={selectedCategory === category.id ? { backgroundColor: printingColors.accent } : {}}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div
                                          className={`w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                                            selectedCategory === category.id
                                              ? 'border-transparent bg-white/20'
                                              : 'border-white/30'
                                          }`}
                                        >
                                          {selectedCategory === category.id && <Check className="w-3 h-3" />}
                                        </div>
                                        {category.label}
                                      </div>
                                      <span className="text-xs text-white/50">{count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Apply button */}
                    <div className="p-5 border-t border-white/10 flex-shrink-0" style={{ backgroundColor: '#1c1c1c' }}>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className="w-full py-3 rounded-[10px] neuzeit-font font-semibold text-white transition-all"
                        style={{ backgroundColor: printingColors.accent }}
                      >
                        Show {filteredProducts.length} Products
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Product Grid */}
            <main className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <p className="neuzeit-font text-sm text-white">
                  <span className="font-semibold">{filteredProducts.length}</span> products
                </p>
              </div>

              <AnimatePresence mode="wait">
                {filteredProducts.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-20 rounded-[15px] border border-white/10"
                    style={{ backgroundColor: '#1c1c1c' }}
                  >
                    <p className="text-white/70 text-lg mb-4 neuzeit-light-font">
                      No products match your filters
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="neuzeit-font text-sm font-medium px-5 py-2.5 rounded-[10px] border border-white/10 text-white transition-all hover:opacity-80"
                      style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                    >
                      Clear all filters
                    </button>
                  </motion.div>
                ) : (
                  <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredProducts.map((product, index) => (
                      <ProductGridCard
                        key={product.slug}
                        product={product}
                        index={index}
                        onClick={() => setModalProduct(product)}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </main>
          </div>
        </div>

        {/* Product Detail Modal */}
        {modalProduct && (
          <ProductDetailModal
            product={modalProduct}
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
          />
        )}
      </div>
    </>
  );
};

// Simple Product Card Component
interface ProductGridCardProps {
  product: PrintingProduct;
  index: number;
  onClick: () => void;
}

const ProductGridCard: React.FC<ProductGridCardProps> = ({
  product,
  index,
  onClick,
}) => {
  const imageUrl = product.images[0] || '/printing/placeholder.jpg';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.2) }}
    >
      <button
        onClick={onClick}
        className="group w-full text-left rounded-[10px] overflow-hidden transition-shadow duration-150 hover:shadow-lg border border-white/10"
        style={{ backgroundColor: '#1c1c1c' }}
      >
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={imageUrl}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-3">
          <h3 className="hearns-font text-sm md:text-base text-white mb-0.5 group-hover:text-[#64a70b] transition-colors line-clamp-2">
            {product.title}
          </h3>
        </div>
      </button>
    </motion.div>
  );
};

export default PrintingBrowser;
